import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, getModuleConfigs, updateModuleConfigs, updateDebugIconPreference, updateFloatingChatPreference, updateQuickCommandPreference, getDaysSectionConfigs, updateDaysSectionConfigs, DaysSectionConfig, updateBirthDate, updateLifeExpectancy } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { useUISettings } from '../contexts/UISettingsContext';
import { MenuSection, MenuItem } from '../types/sidebarTypes';
import moduleConfigs from '../config/moduleConfigs.json';
import './Settings.css';

interface SettingsProps {
  user: User | null;
  onSignOut: () => void;
  onToggleSidebar: () => void;
}

const DEFAULT_MODULES = ['lifetime', 'habits', 'goals', 'today', 'tasks'];

const Settings: React.FC<SettingsProps> = ({ user, onSignOut, onToggleSidebar }) => {
  const { updateUser } = useAuth();
  const { settings: uiSettings, toggleShowHiddenContent, toggleShowFloatingChat, toggleShowQuickCommand } = useUISettings();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'account' | 'modules' | 'developer' | 'days-page'>('modules');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [showDebugIcon, setShowDebugIcon] = useState<boolean>(false);
  const [showFloatingChat, setShowFloatingChat] = useState<boolean>(true);
  const [showQuickCommand, setShowQuickCommand] = useState<boolean>(true);
  const [daysSectionConfig, setDaysSectionConfig] = useState<DaysSectionConfig>({
    habits: true,
    tasks: true,
    goals: false,
    schedule: false,
    onGoingWorks: {
      enabled: false,
      novels: false,
      knowledge: false,
      books: false,
      essays: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState<string>('');
  const [isEditingBirthDate, setIsEditingBirthDate] = useState(false);
  const [lifeExpectancy, setLifeExpectancy] = useState<number>(70);
  const [isEditingLifeExpectancy, setIsEditingLifeExpectancy] = useState(false);

  const config = moduleConfigs as { menuSections: MenuSection[] };

  useEffect(() => {
    if (user) {
      loadModuleConfigs();
      loadDaysSectionConfigs();
      if (user.birth_date) {
        setBirthDate(user.birth_date);
      }
      if (user.life_expectancy) {
        setLifeExpectancy(user.life_expectancy);
      } else {
        setLifeExpectancy(70);
      }
    }
  }, [user]);

  const loadModuleConfigs = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const moduleIds = await getModuleConfigs();
      // Use user's configs if available, otherwise use defaults
      setSelectedModules(moduleIds.length > 0 ? moduleIds : DEFAULT_MODULES);
      // Load debug icon preference
      setShowDebugIcon(user.show_debug_icon ?? false);
      setShowFloatingChat(user.show_floating_chat ?? true);
      setShowQuickCommand(user.show_quick_command ?? true);
    } catch (err) {
      console.error('Failed to load module configs:', err);
      setError('Failed to load module settings');
      // Fallback to defaults on error
      setSelectedModules(DEFAULT_MODULES);
      setShowDebugIcon(false);
      setShowFloatingChat(true);
      setShowQuickCommand(true);
    } finally {
      setLoading(false);
    }
  };

  const loadDaysSectionConfigs = async () => {
    if (!user) return;
    
    try {
      const config = await getDaysSectionConfigs();
      if (config) {
        setDaysSectionConfig({
          habits: config.habits ?? true,
          tasks: config.tasks ?? true,
          goals: config.goals ?? false,
          schedule: config.schedule ?? false,
          onGoingWorks: {
            enabled: config.onGoingWorks?.enabled ?? false,
            novels: config.onGoingWorks?.novels ?? false,
            knowledge: config.onGoingWorks?.knowledge ?? false,
            books: config.onGoingWorks?.books ?? false,
            essays: config.onGoingWorks?.essays ?? false
          }
        });
      } else {
        // Use defaults if no config exists
        setDaysSectionConfig({
          habits: true,
          tasks: true,
          goals: false,
          onGoingWorks: {
            enabled: false,
            novels: false,
            knowledge: false,
            books: false,
            essays: false
          }
        });
      }
    } catch (err) {
      console.error('Failed to load days section configs:', err);
      // Use defaults on error
      setDaysSectionConfig({
        habits: true,
        tasks: true,
        goals: false,
        onGoingWorks: {
          enabled: false,
          novels: false,
          knowledge: false,
          books: false,
          essays: false
        }
      });
    }
  };

  const handleModuleToggle = async (moduleId: string) => {
    if (!user) return;

    // Store previous state for potential revert
    const previousModules = [...selectedModules];
    
    // Calculate new modules array
    const newModules = selectedModules.includes(moduleId)
      ? selectedModules.filter(id => id !== moduleId)
      : [...selectedModules, moduleId];

    // Update state immediately for responsive UI
    setSelectedModules(newModules);
    setSaveSuccess(false);
    setError(null);
    setSaving(true);

    try {
      const updatedUser = await updateModuleConfigs(newModules);
      // Update the user state in AuthContext so Sidebar reflects the changes
      updateUser(updatedUser);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save module configs:', err);
      setError('Failed to save module settings');
      // Revert to previous state on error
      setSelectedModules(previousModules);
    } finally {
      setSaving(false);
    }
  };

  const handleDebugIconToggle = async () => {
    if (!user) return;

    // Store previous state for potential revert
    const previousValue = showDebugIcon;
    
    // Calculate new value
    const newValue = !showDebugIcon;

    // Update state immediately for responsive UI
    setShowDebugIcon(newValue);
    setSaveSuccess(false);
    setError(null);
    setSaving(true);

    try {
      const updatedUser = await updateDebugIconPreference(newValue);
      // Update the user state in AuthContext
      updateUser(updatedUser);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save debug icon preference:', err);
      setError('Failed to save debug icon preference');
      // Revert to previous state on error
      setShowDebugIcon(previousValue);
    } finally {
      setSaving(false);
    }
  };

  const handleFloatingChatToggle = async () => {
    if (!user) return;
    const previousValue = showFloatingChat;
    const newValue = !showFloatingChat;
    setShowFloatingChat(newValue);
    setSaveSuccess(false);
    setError(null);
    setSaving(true);
    try {
      const updatedUser = await updateFloatingChatPreference(newValue);
      updateUser(updatedUser);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save floating chat preference:', err);
      setError('Failed to save floating chat preference');
      setShowFloatingChat(previousValue);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCommandToggle = async () => {
    if (!user) return;
    const previousValue = showQuickCommand;
    const newValue = !showQuickCommand;
    setShowQuickCommand(newValue);
    setSaveSuccess(false);
    setError(null);
    setSaving(true);
    try {
      const updatedUser = await updateQuickCommandPreference(newValue);
      updateUser(updatedUser);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save quick command preference:', err);
      setError('Failed to save quick command preference');
      setShowQuickCommand(previousValue);
    } finally {
      setSaving(false);
    }
  };

  const handleDaysSectionToggle = async (section: 'habits' | 'tasks' | 'goals' | 'schedule', value: boolean) => {
    if (!user) return;

    const previousConfig = { ...daysSectionConfig };
    const newConfig: DaysSectionConfig = {
      ...daysSectionConfig,
      [section]: value
    };

    setDaysSectionConfig(newConfig);
    setSaveSuccess(false);
    setError(null);
    setSaving(true);

    try {
      const updatedUser = await updateDaysSectionConfigs(newConfig);
      updateUser(updatedUser);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save days section config:', err);
      setError('Failed to save days section settings');
      setDaysSectionConfig(previousConfig);
    } finally {
      setSaving(false);
    }
  };

  const handleOnGoingWorksToggle = async (field: 'enabled' | 'novels' | 'knowledge' | 'books' | 'essays', value: boolean) => {
    if (!user) return;

    const previousConfig = { ...daysSectionConfig };
    const newConfig: DaysSectionConfig = {
      ...daysSectionConfig,
      onGoingWorks: {
        ...daysSectionConfig.onGoingWorks,
        [field]: value
      }
    };

    // If disabling the parent, disable all subsections
    if (field === 'enabled' && !value) {
      newConfig.onGoingWorks = {
        enabled: false,
        novels: false,
        knowledge: false,
        books: false,
        essays: false
      };
    }

    setDaysSectionConfig(newConfig);
    setSaveSuccess(false);
    setError(null);
    setSaving(true);

    try {
      const updatedUser = await updateDaysSectionConfigs(newConfig);
      updateUser(updatedUser);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save days section config:', err);
      setError('Failed to save days section settings');
      setDaysSectionConfig(previousConfig);
    } finally {
      setSaving(false);
    }
  };

  const handleBirthDateSave = async () => {
    if (!user) return;

    const previousBirthDate = user.birth_date || '';
    const newBirthDate = birthDate.trim() || null;

    setSaveSuccess(false);
    setError(null);
    setSaving(true);

    try {
      const updatedUser = await updateBirthDate(newBirthDate);
      updateUser(updatedUser);
      setIsEditingBirthDate(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save birth date:', err);
      setError('Failed to save birth date');
      setBirthDate(previousBirthDate);
    } finally {
      setSaving(false);
    }
  };

  const handleBirthDateCancel = () => {
    if (user?.birth_date) {
      setBirthDate(user.birth_date);
    } else {
      setBirthDate('');
    }
    setIsEditingBirthDate(false);
  };

  const handleLifeExpectancySave = async () => {
    if (!user) return;

    const previousLifeExpectancy = user.life_expectancy ?? 70;
    const newLifeExpectancy = lifeExpectancy || null;

    setSaveSuccess(false);
    setError(null);
    setSaving(true);

    try {
      const updatedUser = await updateLifeExpectancy(newLifeExpectancy);
      updateUser(updatedUser);
      setIsEditingLifeExpectancy(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save life expectancy:', err);
      setError('Failed to save life expectancy');
      setLifeExpectancy(previousLifeExpectancy);
    } finally {
      setSaving(false);
    }
  };

  const handleLifeExpectancyCancel = () => {
    if (user?.life_expectancy) {
      setLifeExpectancy(user.life_expectancy);
    } else {
      setLifeExpectancy(70);
    }
    setIsEditingLifeExpectancy(false);
  };

  const isModuleSelected = (moduleId: string): boolean => {
    return selectedModules.includes(moduleId);
  };

  const handleOpenModule = (item: MenuItem) => {
    if (item.specialHandler === 'navigateToToday') {
      // Handle special navigation for today
      const today = new Date().toISOString().split('T')[0];
      navigate(`/days/${today}`);
    } else {
      navigate(item.path);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
        </div>
        <h1 className="settings-title">Settings</h1>
      </div>
      
      <div className="settings-tabs">
        {user && (
          <button
            className={`settings-tab ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('modules')}
          >
            Sidebar Modules
          </button>
        )}
        {user && (
          <button
            className={`settings-tab ${activeTab === 'days-page' ? 'active' : ''}`}
            onClick={() => setActiveTab('days-page')}
          >
            Days Page
          </button>
        )}
        {user && (
          <button
            className={`settings-tab ${activeTab === 'developer' ? 'active' : ''}`}
            onClick={() => setActiveTab('developer')}
          >
            Developer
          </button>
        )}
        <button
          className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          Account
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'account' && (
          <div className="settings-section">
            <h2 className="settings-section-title">Account</h2>
            
            <div className="settings-item">
              <label className="settings-label">Email</label>
              <div className="settings-value">{user?.email || 'Not signed in'}</div>
            </div>
            
            {user && (
              <div className="settings-item">
                <label className="settings-label">Birth Date</label>
                {isEditingBirthDate ? (
                  <div className="settings-edit-group">
                    <input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="settings-input"
                    />
                    <div className="settings-edit-actions">
                      <button
                        className="settings-save-btn"
                        onClick={handleBirthDateSave}
                        disabled={saving}
                      >
                        Save
                      </button>
                      <button
                        className="settings-cancel-btn"
                        onClick={handleBirthDateCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="settings-value-group">
                    <div className="settings-value">
                      {user.birth_date ? new Date(user.birth_date).toLocaleDateString() : 'Not set'}
                    </div>
                    <button
                      className="settings-edit-btn"
                      onClick={() => setIsEditingBirthDate(true)}
                    >
                      {user.birth_date ? 'Edit' : 'Set'}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {user && (
              <div className="settings-item">
                <label className="settings-label">Life Expectancy</label>
                {isEditingLifeExpectancy ? (
                  <div className="settings-edit-group">
                    <input
                      type="number"
                      value={lifeExpectancy}
                      onChange={(e) => setLifeExpectancy(parseInt(e.target.value) || 70)}
                      min="1"
                      max="150"
                      className="settings-input"
                    />
                    <div className="settings-edit-actions">
                      <button
                        className="settings-save-btn"
                        onClick={handleLifeExpectancySave}
                        disabled={saving}
                      >
                        Save
                      </button>
                      <button
                        className="settings-cancel-btn"
                        onClick={handleLifeExpectancyCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="settings-value-group">
                    <div className="settings-value">
                      {user.life_expectancy ? `${user.life_expectancy} years` : '70 years (default)'}
                    </div>
                    <button
                      className="settings-edit-btn"
                      onClick={() => setIsEditingLifeExpectancy(true)}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {user && (
              <div className="settings-item">
                <button className="settings-signout-btn" onClick={onSignOut}>
                  🚪 Sign Out
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'modules' && user && (
          <div className="settings-section">
            <h2 className="settings-section-title">Sidebar Modules</h2>
            <p className="settings-description">
              Select which modules to display in the sidebar. Click the link icon to open a module directly.
            </p>

            {loading ? (
              <div className="settings-loading">Loading modules...</div>
            ) : (
              <>
                {config.menuSections
                  .filter(section => section.id !== 'home') // Exclude home section
                  .map(section => {
                    // Only show section if it has items
                    if (section.items.length === 0) return null;

                    return (
                      <div key={section.id} className="module-section">
                        {section.title && (
                          <h3 className="module-section-title">{section.title}</h3>
                        )}
                        <div className="module-items">
                          {section.items.map((item: MenuItem) => (
                            <div key={item.id} className="module-item-wrapper">
                              <label className="module-item">
                                <input
                                  type="checkbox"
                                  checked={isModuleSelected(item.id)}
                                  onChange={() => handleModuleToggle(item.id)}
                                  className="module-checkbox"
                                />
                                <span className="module-label">{item.label}</span>
                              </label>
                              <button
                                className="module-open-btn"
                                onClick={() => handleOpenModule(item)}
                                title={`Open ${item.label}`}
                              >
                                ↗
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                {error && (
                  <div className="settings-error">{error}</div>
                )}

                {saving && (
                  <div className="settings-loading">Saving...</div>
                )}

                {saveSuccess && (
                  <div className="settings-success">Module settings saved successfully!</div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'days-page' && user && (
          <div className="settings-section">
            <h2 className="settings-section-title">Days Page Sections</h2>
            <p className="settings-description">
              Configure which sections appear on the Days page. Changes are saved automatically.
            </p>

            {/* Main Sections */}
            <div className="module-items">
              <div className="module-item-wrapper">
                <label className="module-item">
                  <input
                    type="checkbox"
                    checked={daysSectionConfig.habits ?? true}
                    onChange={(e) => handleDaysSectionToggle('habits', e.target.checked)}
                    className="module-checkbox"
                  />
                  <span className="module-label">Habits</span>
                </label>
              </div>
              <div className="module-item-wrapper">
                <label className="module-item">
                  <input
                    type="checkbox"
                    checked={daysSectionConfig.tasks ?? true}
                    onChange={(e) => handleDaysSectionToggle('tasks', e.target.checked)}
                    className="module-checkbox"
                  />
                  <span className="module-label">Tasks</span>
                </label>
              </div>
              <div className="module-item-wrapper">
                <label className="module-item">
                  <input
                    type="checkbox"
                    checked={daysSectionConfig.goals ?? false}
                    onChange={(e) => handleDaysSectionToggle('goals', e.target.checked)}
                    className="module-checkbox"
                  />
                  <span className="module-label">Goals</span>
                </label>
              </div>
              <div className="module-item-wrapper">
                <label className="module-item">
                  <input
                    type="checkbox"
                    checked={daysSectionConfig.schedule ?? false}
                    onChange={(e) => handleDaysSectionToggle('schedule', e.target.checked)}
                    className="module-checkbox"
                  />
                  <span className="module-label">Schedule</span>
                </label>
              </div>
            </div>

            {/* On-going Works Section */}
            <div className="module-section" style={{ marginTop: '1.5rem' }}>
              <h3 className="module-section-title">On-going Works</h3>
              <div className="module-items">
                <div className="module-item-wrapper">
                  <label className="module-item">
                    <input
                      type="checkbox"
                      checked={daysSectionConfig.onGoingWorks?.enabled ?? false}
                      onChange={(e) => handleOnGoingWorksToggle('enabled', e.target.checked)}
                      className="module-checkbox"
                    />
                    <span className="module-label">Enable On-going Works</span>
                  </label>
                </div>
                {daysSectionConfig.onGoingWorks?.enabled && (
                  <>
                    <div className="module-item-wrapper" style={{ paddingLeft: '1.5rem' }}>
                      <label className="module-item">
                        <input
                          type="checkbox"
                          checked={daysSectionConfig.onGoingWorks?.novels ?? false}
                          onChange={(e) => handleOnGoingWorksToggle('novels', e.target.checked)}
                          className="module-checkbox"
                        />
                        <span className="module-label">Novels</span>
                      </label>
                    </div>
                    <div className="module-item-wrapper" style={{ paddingLeft: '1.5rem' }}>
                      <label className="module-item">
                        <input
                          type="checkbox"
                          checked={daysSectionConfig.onGoingWorks?.knowledge ?? false}
                          onChange={(e) => handleOnGoingWorksToggle('knowledge', e.target.checked)}
                          className="module-checkbox"
                        />
                        <span className="module-label">Knowledge</span>
                      </label>
                    </div>
                    <div className="module-item-wrapper" style={{ paddingLeft: '1.5rem' }}>
                      <label className="module-item">
                        <input
                          type="checkbox"
                          checked={daysSectionConfig.onGoingWorks?.books ?? false}
                          onChange={(e) => handleOnGoingWorksToggle('books', e.target.checked)}
                          className="module-checkbox"
                        />
                        <span className="module-label">Books</span>
                      </label>
                    </div>
                    <div className="module-item-wrapper" style={{ paddingLeft: '1.5rem' }}>
                      <label className="module-item">
                        <input
                          type="checkbox"
                          checked={daysSectionConfig.onGoingWorks?.essays ?? false}
                          onChange={(e) => handleOnGoingWorksToggle('essays', e.target.checked)}
                          className="module-checkbox"
                        />
                        <span className="module-label">Essays</span>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>

            {error && (
              <div className="settings-error">{error}</div>
            )}

            {saving && (
              <div className="settings-loading">Saving...</div>
            )}

            {saveSuccess && (
              <div className="settings-success">Settings saved successfully!</div>
            )}
          </div>
        )}

        {activeTab === 'developer' && user && (
          <div className="settings-section">
            <h2 className="settings-section-title">Developer</h2>
            <p className="settings-description">
              Developer tools and debugging options.
            </p>

            {/* Show Hidden Content Toggle */}
            <div className="settings-item">
              <label className="settings-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span>Show Hidden Content</span>
                <input
                  type="checkbox"
                  checked={uiSettings.showHiddenContent}
                  onChange={toggleShowHiddenContent}
                  className="module-checkbox"
                  style={{ cursor: 'pointer' }}
                />
              </label>
              <p className="settings-description" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666666' }}>
                Show hidden novels and content. Session-only — resets when you close the app.
              </p>
            </div>

            {/* Floating Chat Toggle */}
            <div className="settings-item">
              <label className="settings-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span>Show Floating Chat</span>
                <input
                  type="checkbox"
                  checked={showFloatingChat}
                  onChange={handleFloatingChatToggle}
                  className="module-checkbox"
                  style={{ cursor: 'pointer' }}
                />
              </label>
              <p className="settings-description" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666666' }}>
                Show or hide the floating AI chat icon.
              </p>
            </div>

            {/* Quick Command Toggle */}
            <div className="settings-item">
              <label className="settings-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span>Show Quick Command</span>
                <input
                  type="checkbox"
                  checked={showQuickCommand}
                  onChange={handleQuickCommandToggle}
                  className="module-checkbox"
                  style={{ cursor: 'pointer' }}
                />
              </label>
              <p className="settings-description" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666666' }}>
                Show or hide the quick command icon.
              </p>
            </div>

            {/* Debug Icon Toggle */}
            <div className="settings-item">
              <label className="settings-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span>Show Debug Icon</span>
                <input
                  type="checkbox"
                  checked={showDebugIcon}
                  onChange={handleDebugIconToggle}
                  className="module-checkbox"
                  style={{ cursor: 'pointer' }}
                />
              </label>
              <p className="settings-description" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666666' }}>
                Toggle the visibility of the debug icon in the application.
              </p>
            </div>

            {error && (
              <div className="settings-error">{error}</div>
            )}

            {saving && (
              <div className="settings-loading">Saving...</div>
            )}

            {saveSuccess && (
              <div className="settings-success">Settings saved successfully!</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
